import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building2, ArrowRight, ArrowLeft, Loader2, CheckCircle, Globe, Calendar, MapPin, Briefcase, Users, FileText, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  'India', 'United States', 'United Kingdom', 'United Arab Emirates', 'Singapore',
  'Australia', 'Canada', 'Germany', 'France', 'Japan', 'South Korea', 'China',
  'Netherlands', 'Switzerland', 'Ireland', 'Hong Kong', 'Malaysia', 'Indonesia',
  'Thailand', 'Philippines', 'Vietnam', 'Brazil', 'Mexico', 'South Africa', 'Other'
];

const INDUSTRIES = [
  'Information Technology', 'Financial Services', 'Healthcare', 'Manufacturing',
  'Retail & E-commerce', 'Consulting', 'Education', 'Real Estate', 'Energy & Utilities',
  'Telecommunications', 'Automotive', 'Aerospace & Defense', 'Pharmaceuticals',
  'Media & Entertainment', 'Hospitality', 'Transportation & Logistics',
  'Construction', 'Agriculture', 'Government', 'Non-Profit', 'Other'
];

const COMPANY_SIZES = [
  { value: '1-10', label: 'Startup (1-10 employees)' },
  { value: '11-50', label: 'Small (11-50 employees)' },
  { value: '51-200', label: 'Medium (51-200 employees)' },
  { value: '201-500', label: 'Large (201-500 employees)' },
  { value: '501-1000', label: 'Enterprise (501-1000 employees)' },
  { value: '1000+', label: 'Global Enterprise (1000+ employees)' }
];

export const CorporateRegistrationPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [step, setStep] = useState(1); // 1: Company Details, 2: Admin Account
  const [loading, setLoading] = useState(false);
  const [organizationId, setOrganizationId] = useState(null);
  
  // Company Details
  const [companyData, setCompanyData] = useState({
    company_name: '',
    incorporation_date: '',
    website: '',
    country: '',
    industry: '',
    company_size: '',
    registered_address: '',
    gst_tax_id: ''
  });

  // Admin Account
  const [adminData, setAdminData] = useState({
    full_name: '',
    department: '',
    email: '',
    mobile_number: '',
    username: '',
    password: '',
    confirm_password: ''
  });

  const [otpData, setOtpData] = useState({
    emailOtp: '',
    mobileOtp: '',
    adminId: null,
    devEmailOtp: '',
    devMobileOtp: ''
  });

  const handleCompanyChange = (field, value) => {
    setCompanyData(prev => ({ ...prev, [field]: value }));
  };

  const handleAdminChange = (field, value) => {
    setAdminData(prev => ({ ...prev, [field]: value }));
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!companyData.company_name || !companyData.country || !companyData.industry || !companyData.company_size || !companyData.registered_address) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/enterprise/organization/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(companyData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      setOrganizationId(data.organization_id);
      toast.success('Organization registered successfully!');
      setStep(2);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!adminData.full_name || !adminData.email || !adminData.mobile_number || !adminData.username || !adminData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (adminData.password !== adminData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    // Check for personal email
    const blockedDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'icloud.com'];
    const emailDomain = adminData.email.split('@')[1]?.toLowerCase();
    if (blockedDomains.includes(emailDomain)) {
      toast.error('Please use your corporate email address. Personal emails are not allowed.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/enterprise/admin/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...adminData,
          organization_id: organizationId
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Admin registration failed');
      }

      setOtpData({
        adminId: data.admin_id,
        devEmailOtp: data._dev_email_otp || '',
        devMobileOtp: data._dev_mobile_otp || '',
        emailOtp: '',
        mobileOtp: ''
      });
      
      toast.success('Admin account created! Please verify your email and mobile.');
      setStep(3);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (type) => {
    const otp = type === 'email' ? otpData.emailOtp : otpData.mobileOtp;
    const target = type === 'email' ? adminData.email : adminData.mobile_number;

    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/enterprise/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_type: type,
          target_value: target,
          otp_code: otp,
          purpose: 'registration'
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Verification failed');
      }

      toast.success(`${type === 'email' ? 'Email' : 'Mobile'} verified successfully!`);
      
      if (type === 'email') {
        setOtpData(prev => ({ ...prev, emailVerified: true }));
      } else {
        setOtpData(prev => ({ ...prev, mobileVerified: true }));
      }

      // If both verified, redirect to login
      if ((type === 'email' && otpData.mobileVerified) || (type === 'mobile' && otpData.emailVerified)) {
        toast.success('All verifications complete! Redirecting to login...');
        setTimeout(() => navigate('/corporate/login'), 2000);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-[#1a1a2e] to-gray-900 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl"></div>
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 p-6 bg-gray-900/80 backdrop-blur-lg border-b border-gray-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => step > 1 ? setStep(step - 1) : navigate('/gateway')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-white">Corporate Registration</span>
          </div>
          <LanguageSelector variant="minimal" />
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 min-h-screen flex items-center justify-center px-4 pt-24 pb-12">
        <div className="max-w-2xl w-full">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-center gap-4">
              {[1, 2, 3].map((stepNum) => (
                <div key={stepNum} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                    step >= stepNum 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-700 text-gray-400'
                  }`}>
                    {step > stepNum ? <CheckCircle className="w-5 h-5" /> : stepNum}
                  </div>
                  {stepNum < 3 && (
                    <div className={`w-20 h-1 mx-2 ${step > stepNum ? 'bg-blue-500' : 'bg-gray-700'}`} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-4 mt-2 text-sm text-gray-400">
              <span className="w-24 text-center">Company</span>
              <span className="w-20"></span>
              <span className="w-24 text-center">Admin</span>
              <span className="w-20"></span>
              <span className="w-24 text-center">Verify</span>
            </div>
          </div>

          {/* Step 1: Company Details */}
          {step === 1 && (
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Building2 className="w-6 h-6 text-blue-500" />
                  Company Details
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Register your organization to get started with LeadEconomics™ Enterprise
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCompanySubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label className="text-gray-300">Company Name *</Label>
                      <Input
                        value={companyData.company_name}
                        onChange={(e) => handleCompanyChange('company_name', e.target.value)}
                        placeholder="Enter company name"
                        className="bg-gray-900 border-gray-700 text-white"
                        required
                      />
                    </div>

                    <div>
                      <Label className="text-gray-300">Incorporation Date</Label>
                      <Input
                        type="date"
                        value={companyData.incorporation_date}
                        onChange={(e) => handleCompanyChange('incorporation_date', e.target.value)}
                        className="bg-gray-900 border-gray-700 text-white"
                      />
                    </div>

                    <div>
                      <Label className="text-gray-300">Website</Label>
                      <Input
                        value={companyData.website}
                        onChange={(e) => handleCompanyChange('website', e.target.value)}
                        placeholder="https://company.com"
                        className="bg-gray-900 border-gray-700 text-white"
                      />
                    </div>

                    <div>
                      <Label className="text-gray-300">Country *</Label>
                      <Select value={companyData.country} onValueChange={(v) => handleCompanyChange('country', v)}>
                        <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-900 border-gray-700">
                          {COUNTRIES.map(country => (
                            <SelectItem key={country} value={country} className="text-white">{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-gray-300">Industry *</Label>
                      <Select value={companyData.industry} onValueChange={(v) => handleCompanyChange('industry', v)}>
                        <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-900 border-gray-700">
                          {INDUSTRIES.map(industry => (
                            <SelectItem key={industry} value={industry} className="text-white">{industry}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-gray-300">Company Size *</Label>
                      <Select value={companyData.company_size} onValueChange={(v) => handleCompanyChange('company_size', v)}>
                        <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-900 border-gray-700">
                          {COMPANY_SIZES.map(size => (
                            <SelectItem key={size.value} value={size.value} className="text-white">{size.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-gray-300">GST / Tax ID (Optional)</Label>
                      <Input
                        value={companyData.gst_tax_id}
                        onChange={(e) => handleCompanyChange('gst_tax_id', e.target.value)}
                        placeholder="Enter GST/Tax ID"
                        className="bg-gray-900 border-gray-700 text-white"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Label className="text-gray-300">Registered Office Address *</Label>
                      <Input
                        value={companyData.registered_address}
                        onChange={(e) => handleCompanyChange('registered_address', e.target.value)}
                        placeholder="Enter complete address"
                        className="bg-gray-900 border-gray-700 text-white"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Registering...
                      </>
                    ) : (
                      <>
                        Continue to Admin Registration
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Admin Account */}
          {step === 2 && (
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Users className="w-6 h-6 text-blue-500" />
                  Company Admin Account
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Create your administrator account. Only corporate email addresses are allowed.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAdminSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label className="text-gray-300">Full Name *</Label>
                      <Input
                        value={adminData.full_name}
                        onChange={(e) => handleAdminChange('full_name', e.target.value)}
                        placeholder="Enter your full name"
                        className="bg-gray-900 border-gray-700 text-white"
                        required
                      />
                    </div>

                    <div>
                      <Label className="text-gray-300">Department *</Label>
                      <Input
                        value={adminData.department}
                        onChange={(e) => handleAdminChange('department', e.target.value)}
                        placeholder="e.g., Human Resources"
                        className="bg-gray-900 border-gray-700 text-white"
                        required
                      />
                    </div>

                    <div>
                      <Label className="text-gray-300">Corporate Email *</Label>
                      <Input
                        type="email"
                        value={adminData.email}
                        onChange={(e) => handleAdminChange('email', e.target.value)}
                        placeholder="you@company.com"
                        className="bg-gray-900 border-gray-700 text-white"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Personal emails (Gmail, Yahoo, etc.) not allowed</p>
                    </div>

                    <div>
                      <Label className="text-gray-300">Mobile Number *</Label>
                      <Input
                        value={adminData.mobile_number}
                        onChange={(e) => handleAdminChange('mobile_number', e.target.value)}
                        placeholder="+91 98765 43210"
                        className="bg-gray-900 border-gray-700 text-white"
                        required
                      />
                    </div>

                    <div>
                      <Label className="text-gray-300">Username *</Label>
                      <Input
                        value={adminData.username}
                        onChange={(e) => handleAdminChange('username', e.target.value)}
                        placeholder="Choose a username"
                        className="bg-gray-900 border-gray-700 text-white"
                        required
                      />
                    </div>

                    <div>
                      <Label className="text-gray-300">Password *</Label>
                      <Input
                        type="password"
                        value={adminData.password}
                        onChange={(e) => handleAdminChange('password', e.target.value)}
                        placeholder="Enter password"
                        className="bg-gray-900 border-gray-700 text-white"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Min 8 chars, uppercase, lowercase, number, special char</p>
                    </div>

                    <div>
                      <Label className="text-gray-300">Confirm Password *</Label>
                      <Input
                        type="password"
                        value={adminData.confirm_password}
                        onChange={(e) => handleAdminChange('confirm_password', e.target.value)}
                        placeholder="Confirm password"
                        className="bg-gray-900 border-gray-700 text-white"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      <>
                        Create Admin Account
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Step 3: OTP Verification */}
          {step === 3 && (
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                  Verify Your Account
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Enter the OTP sent to your email and mobile number
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Dev OTPs (remove in production) */}
                {(otpData.devEmailOtp || otpData.devMobileOtp) && (
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                    <p className="text-yellow-500 text-sm font-medium mb-2">Development Mode - OTPs:</p>
                    <p className="text-gray-300 text-sm">Email OTP: <code className="bg-gray-800 px-2 py-1 rounded">{otpData.devEmailOtp}</code></p>
                    <p className="text-gray-300 text-sm">Mobile OTP: <code className="bg-gray-800 px-2 py-1 rounded">{otpData.devMobileOtp}</code></p>
                  </div>
                )}

                {/* Email OTP */}
                <div className="space-y-3">
                  <Label className="text-gray-300">Email OTP</Label>
                  <div className="flex gap-3">
                    <Input
                      value={otpData.emailOtp}
                      onChange={(e) => setOtpData(prev => ({ ...prev, emailOtp: e.target.value }))}
                      placeholder="Enter 6-digit OTP"
                      maxLength={6}
                      className="bg-gray-900 border-gray-700 text-white flex-1"
                      disabled={otpData.emailVerified}
                    />
                    <Button
                      onClick={() => handleVerifyOtp('email')}
                      disabled={loading || otpData.emailVerified}
                      className={otpData.emailVerified ? 'bg-green-500' : 'bg-blue-500 hover:bg-blue-600'}
                    >
                      {otpData.emailVerified ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Verified
                        </>
                      ) : loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Verify'
                      )}
                    </Button>
                  </div>
                </div>

                {/* Mobile OTP */}
                <div className="space-y-3">
                  <Label className="text-gray-300">Mobile OTP</Label>
                  <div className="flex gap-3">
                    <Input
                      value={otpData.mobileOtp}
                      onChange={(e) => setOtpData(prev => ({ ...prev, mobileOtp: e.target.value }))}
                      placeholder="Enter 6-digit OTP"
                      maxLength={6}
                      className="bg-gray-900 border-gray-700 text-white flex-1"
                      disabled={otpData.mobileVerified}
                    />
                    <Button
                      onClick={() => handleVerifyOtp('mobile')}
                      disabled={loading || otpData.mobileVerified}
                      className={otpData.mobileVerified ? 'bg-green-500' : 'bg-blue-500 hover:bg-blue-600'}
                    >
                      {otpData.mobileVerified ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Verified
                        </>
                      ) : loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Verify'
                      )}
                    </Button>
                  </div>
                </div>

                {/* Skip to Login */}
                <div className="pt-4 border-t border-gray-700">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/corporate/login')}
                    className="w-full border-gray-600 text-gray-300 hover:bg-gray-800"
                  >
                    Skip to Login (if already verified)
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default CorporateRegistrationPage;
